import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  MoreVertical, 
  ChevronDown, 
  Users, 
  Star, 
  RefreshCw, 
  X,
  Eye,
  List,
  Columns,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  Filter,
  Send,
  Upload,
  FileText,
  Plus,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../components/shared/ToastProvider';
import { formatCandidateId } from '../utils/idFormatter';
import SubmitToJobModal from '../components/SubmitToJobModal';

const CandidateDashboard = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([
    'id', 'score', 'name', 'status', 'owner', 'dateAdded', 'source', 'email', 'phone', 'address', 'category', 'actions'
  ]);
  const [sortField, setSortField] = useState('dateAdded');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [viewMode, setViewMode] = useState('table');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  const availableColumns = {
    id: { label: 'ID', key: 'candidate_id' },
    score: { label: 'Sc', key: 'match_score' },
    name: { label: 'Name', key: 'name' },
    status: { label: 'Status', key: 'candidate_status' },
    owner: { label: 'Candidate Owner', key: 'recruited_by' },
    dateAdded: { label: 'Date Added', key: 'created_at' },
    source: { label: 'Source', key: 'source' },
    email: { label: 'Email', key: 'email' },
    phone: { label: 'Candidate Primary Phone', key: 'phone' },
    address: { label: 'Home Address', key: 'location' },
    category: { label: 'Category', key: 'candidate_priority' },
    actions: { label: 'Actions', key: 'actions' }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        setCandidates(data || []);
      } else {
        showToast('Failed to load candidates', 'error');
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      showToast('Error loading candidates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSubmitToJob = async (submissionData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate_id: selectedCandidate.candidate_id,
          job_id: submissionData.job_id,
          job_title: submissionData.job_title,
          recruiter_id: 'current-user-id',
          recruiter_name: 'Current User',
          initial_status: submissionData.status,
          submission_source: submissionData.source,
          submission_notes: submissionData.comments,
          priority_level: submissionData.priority || 'normal',
          match_score: 0.85
        })
      });

      if (response.ok) {
        showToast(`Successfully submitted ${selectedCandidate?.first_name} ${selectedCandidate?.last_name} to ${submissionData.job_title}`, 'success');
        setShowSubmitModal(false);
        setSelectedCandidate(null);
      } else {
        showToast('Failed to submit candidate to job', 'error');
      }
    } catch (error) {
      console.error('Error submitting candidate to job:', error);
      showToast('Error submitting candidate to job', 'error');
    }
  };

  // Resume upload functionality
  const handleFileSelect = (file) => {
    if (file && (file.type === 'application/pdf' || file.type.includes('document') || file.name.endsWith('.pdf') || file.name.endsWith('.doc') || file.name.endsWith('.docx'))) {
      setUploadedFile(file);
      processResume(file);
    } else {
      showToast('Please upload a PDF, DOC, or DOCX file', 'error');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const processResume = async (file) => {
    setIsProcessing(true);
    try {
      // Create FormData to send file to backend
      const formData = new FormData();
      formData.append('file', file);

      // Send file to backend AI parsing service
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api/candidates/parse-resume`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Map AI response to our expected format
          const extractedData = {
            first_name: result.data.first_name || 'Not specified',
            last_name: result.data.last_name || 'Not specified', 
            email: result.data.email || 'Not specified',
            phone: result.data.phone || 'Not specified',
            current_position: result.data.current_position || 'Not specified',
            current_company: result.data.current_company || 'Not specified',
            experience_years: result.data.experience_years || 0,
            skills: result.data.skills || [],
            education: result.data.education || 'Not specified',
            location: result.data.location || 'Not specified',
            summary: result.data.summary || 'Not specified',
            previous_positions: result.data.previous_positions || [],
            certifications: result.data.certifications || [],
            languages: result.data.languages || []
          };
          
          setExtractedData(extractedData);
          showToast('Resume processed successfully! Review the extracted information below.', 'success');
        } else {
          throw new Error('Parsing failed on server side');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to parse resume');
      }
    } catch (error) {
      console.error('Error processing resume:', error);
      showToast(`Failed to process resume: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const createCandidateFromResume = async () => {
    if (!extractedData) return;
    
    try {
      const candidateData = {
        first_name: extractedData.first_name,
        last_name: extractedData.last_name,
        email: extractedData.email,
        phone: extractedData.phone,
        current_position: extractedData.current_position,
        current_company: extractedData.current_company,
        experience_years: extractedData.experience_years,
        skills: extractedData.skills,
        location: extractedData.location,
        source: 'resume_upload',
        candidate_status: 'new_lead',
        recruited_by: 'Current User',
        resume_file_name: uploadedFile?.name,
        // Format education as expected by backend
        education: extractedData.education && extractedData.education !== 'Not specified' ? 
          [{
            degree: extractedData.education,
            institution: 'Not specified',
            year: null
          }] : []
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api/candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(candidateData)
      });

      if (response.ok) {
        showToast('Candidate created successfully from resume!', 'success');
        setShowUploadModal(false);
        setUploadedFile(null);
        setExtractedData(null);
        fetchCandidates(); // Refresh the candidates list
      } else {
        showToast('Failed to create candidate', 'error');
      }
    } catch (error) {
      console.error('Error creating candidate:', error);
      showToast('Error creating candidate', 'error');
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setExtractedData(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const toggleFavorite = (candidateId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(candidateId)) {
      newFavorites.delete(candidateId);
    } else {
      newFavorites.add(candidateId);
    }
    setFavorites(newFavorites);
  };

  const toggleColumn = (columnKey) => {
    if (selectedColumns.includes(columnKey)) {
      setSelectedColumns(selectedColumns.filter(col => col !== columnKey));
    } else {
      setSelectedColumns([...selectedColumns, columnKey]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'new_lead': 'bg-blue-100 text-blue-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'hired': 'bg-purple-100 text-purple-800',
      'declined': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSourceColor = (source) => {
    const colors = {
      'linkedin': 'bg-blue-100 text-blue-800',
      'indeed': 'bg-green-100 text-green-800',
      'dice': 'bg-orange-100 text-orange-800',
      'monster': 'bg-purple-100 text-purple-800',
      'manual': 'bg-gray-100 text-gray-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getScoreBar = (score) => {
    if (!score) return null;
    const percentage = Math.min(score * 100, 100);
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-500 h-2 rounded-full" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const filteredAndSortedCandidates = candidates
    .filter(candidate => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const fullName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
      return (
        fullName.toLowerCase().includes(searchLower) ||
        candidate.email?.toLowerCase().includes(searchLower) ||
        candidate.phone?.toLowerCase().includes(searchLower) ||
        candidate.current_title?.toLowerCase().includes(searchLower) ||
        candidate.current_company?.toLowerCase().includes(searchLower) ||
        candidate.source?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      const column = availableColumns[sortField];
      if (!column) return 0;
      
      let aValue = a[column.key] || '';
      let bValue = b[column.key] || '';
      
      // Handle different data types
      if (column.key === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (column.key === 'match_score') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const renderCell = (candidate, columnKey) => {
    const column = availableColumns[columnKey];
    if (!column) return null;

    switch (columnKey) {
      case 'id':
        return (
          <div className="flex items-center space-x-2">
            <span className="font-medium text-blue-600 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors" 
                  title={`Full ID: ${candidate.candidate_id || candidate.id} (click to copy)`}
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(candidate.candidate_id || candidate.id);
                    }
                  }}>
              {formatCandidateId(candidate.candidate_id || candidate.id)}
            </span>
          </div>
        );

      case 'score':
        return (
          <div className="w-12">
            {getScoreBar(candidate.match_score || Math.random() * 0.8 + 0.2)}
          </div>
        );

      case 'name':
        const fullName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
        return (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <a 
              href={`/candidates/${candidate.id}`} 
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {fullName || 'Unknown Name'}
            </a>
          </div>
        );

      case 'status':
        const status = candidate.candidate_status || 'active';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
            {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        );

      case 'owner':
        return (
          <span className="text-gray-900">
            {candidate.recruited_by || 'Unassigned'}
          </span>
        );

      case 'dateAdded':
        return (
          <span className="text-gray-600">
            {formatDate(candidate.created_at)}
          </span>
        );

      case 'source':
        const source = candidate.source || 'manual';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(source)}`}>
            {source.charAt(0).toUpperCase() + source.slice(1)}
          </span>
        );

      case 'email':
        return (
          <a 
            href={`mailto:${candidate.email}`} 
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <Mail className="w-3 h-3" />
            <span>{candidate.email}</span>
          </a>
        );

      case 'phone':
        const phone = candidate.phone || candidate.mobile_phone;
        return phone ? (
          <a 
            href={`tel:${phone}`} 
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <Phone className="w-3 h-3" />
            <span>{phone}</span>
          </a>
        ) : (
          <span className="text-gray-400">No phone</span>
        );

      case 'address':
        return (
          <div className="flex items-center space-x-1">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span className="text-gray-600">{candidate.location || 'Not specified'}</span>
          </div>
        );

      case 'category':
        const priority = candidate.candidate_priority || 'medium';
        const priorityColors = {
          'low': 'bg-gray-100 text-gray-800',
          'medium': 'bg-blue-100 text-blue-800',
          'high': 'bg-orange-100 text-orange-800',
          'hot': 'bg-red-100 text-red-800'
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority]}`}>
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </span>
        );

      case 'actions':
        return (
          <div className="flex items-center justify-center">
            <button
              onClick={() => {
                setSelectedCandidate(candidate);
                setShowSubmitModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center space-x-1"
              title="Submit to Job"
            >
              <Send className="w-3 h-3" />
              <span>Submit</span>
            </button>
          </div>
        );

      default:
        return <span>{candidate[column.key] || '-'}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className="bg-blue-800 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-500 p-2 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-semibold">Candidates</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Upload Resume Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Resume</span>
            </button>

            {/* View Toggle */}
            <button
              onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
              className="p-2 hover:bg-blue-700 rounded transition-colors"
              title="Switch view"
            >
              <List className="w-5 h-5" />
            </button>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white text-gray-900 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>

            {/* Column Selector */}
            <div className="relative">
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="p-2 hover:bg-blue-700 rounded transition-colors"
                title="Customize columns"
              >
                <Columns className="w-5 h-5" />
              </button>
              
              {showColumnSelector && (
                <div className="absolute right-0 top-full mt-2 bg-white text-gray-900 border border-gray-200 rounded-lg shadow-lg p-4 w-64 z-10">
                  <h3 className="font-medium mb-3">Select Columns</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.entries(availableColumns).map(([key, column]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedColumns.includes(key)}
                          onChange={() => toggleColumn(key)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{column.label}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowColumnSelector(false)}
                    className="mt-3 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Users */}
            <button className="p-2 hover:bg-blue-700 rounded transition-colors" title="User management">
              <Users className="w-5 h-5" />
            </button>

            {/* Favorites */}
            <button className="p-2 hover:bg-blue-700 rounded transition-colors" title="Favorites">
              <Star className="w-5 h-5" />
            </button>

            {/* Refresh */}
            <button
              onClick={fetchCandidates}
              className="p-2 hover:bg-blue-700 rounded transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            {/* Close */}
            <button className="p-2 hover:bg-blue-700 rounded transition-colors" title="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Column Headers Bar */}
      <div className="bg-blue-100 border-b-4 border-green-500">
        <div className="px-6 py-3">
          <div className="grid gap-4 items-center" style={{ gridTemplateColumns: selectedColumns.map(columnKey => {
            const column = availableColumns[columnKey];
            switch(columnKey) {
              case 'id': case 'score': case 'source': case 'status': case 'category': case 'actions': 
                return 'minmax(80px, 1fr)';
              case 'dateAdded': 
                return 'minmax(120px, 1fr)';
              case 'name': case 'owner': case 'email': case 'phone': case 'address': 
                return 'minmax(180px, 2fr)';
              default: 
                return 'minmax(100px, 1fr)';
            }
          }).join(' ') }}>
            {selectedColumns.map(columnKey => {
              const column = availableColumns[columnKey];
              if (!column) return null;
              
              return (
                <div 
                  key={columnKey} 
                  className="flex items-center space-x-1 cursor-pointer hover:bg-blue-200 p-2 rounded"
                  onClick={() => handleSort(columnKey)}
                >
                  <span className="font-medium text-gray-800 text-sm">{column.label}</span>
                  <ChevronDown className={`w-3 h-3 text-gray-600 transform transition-transform ${
                    sortField === columnKey && sortDirection === 'desc' ? 'rotate-180' : ''
                  }`} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading candidates...</p>
            </div>
          </div>
        ) : filteredAndSortedCandidates.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search criteria.' : 'No candidates have been added yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="px-6">
              {filteredAndSortedCandidates.map((candidate, index) => (
                <div 
                  key={candidate.id} 
                  className={`grid gap-4 items-center py-3 border-b border-gray-100 hover:bg-gray-50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                  style={{ gridTemplateColumns: selectedColumns.map(columnKey => {
                    switch(columnKey) {
                      case 'id': case 'score': case 'source': case 'status': case 'category': case 'actions': 
                        return 'minmax(80px, 1fr)';
                      case 'dateAdded': 
                        return 'minmax(120px, 1fr)';
                      case 'name': case 'owner': case 'email': case 'phone': case 'address': 
                        return 'minmax(180px, 2fr)';
                      default: 
                        return 'minmax(100px, 1fr)';
                    }
                  }).join(' ') }}
                >
                  {selectedColumns.map(columnKey => {
                    const column = availableColumns[columnKey];
                    if (!column) return null;
                    
                    return (
                      <div key={columnKey} className="text-sm">
                        {renderCell(candidate, columnKey)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{filteredAndSortedCandidates.length} candidates</span>
          <span>
            {searchTerm && `Filtered by: "${searchTerm}"`}
            {sortField && ` • Sorted by: ${availableColumns[sortField]?.label}`}
          </span>
        </div>
      </div>

      {/* Submit to Job Modal */}
      {showSubmitModal && selectedCandidate && (
        <SubmitToJobModal
          isOpen={showSubmitModal}
          candidate={selectedCandidate}
          onSubmit={handleSubmitToJob}
          onClose={() => {
            setShowSubmitModal(false);
            setSelectedCandidate(null);
          }}
        />
      )}

      {/* Upload Resume Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Upload Resume</h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUpload();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-gray-600 mt-1">
                Upload a resume to automatically create a candidate profile with extracted information
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* File Upload Area */}
              {!uploadedFile && (
                <div className="space-y-4">
                  {/* Drag & Drop Area */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragOver 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Drag & Drop Resume Here
                    </h3>
                    <p className="text-gray-500 mb-4">
                      or click to browse and select a file
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Browse Files</span>
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      Supported formats: PDF, DOC, DOCX (Max 10MB)
                    </p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>
              )}

              {/* File Processing */}
              {uploadedFile && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-800">{uploadedFile.name}</h4>
                        <p className="text-sm text-blue-600">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      {isProcessing ? (
                        <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                      ) : extractedData ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <button
                          onClick={resetUpload}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    
                    {isProcessing && (
                      <div className="mt-3">
                        <p className="text-sm text-blue-700 mb-2">Processing resume...</p>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full animate-pulse w-3/4"></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Extracted Information */}
                  {extractedData && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-800">Information Extracted Successfully</h4>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Name:</span> 
                          <span className="ml-1">{extractedData.first_name} {extractedData.last_name}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Email:</span> 
                          <span className="ml-1">{extractedData.email}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Phone:</span> 
                          <span className="ml-1">{extractedData.phone}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Location:</span> 
                          <span className="ml-1">{extractedData.location}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Current Role:</span> 
                          <span className="ml-1">{extractedData.current_position}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Company:</span> 
                          <span className="ml-1">{extractedData.current_company}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">Skills:</span> 
                          <div className="mt-1 flex flex-wrap gap-1">
                            {extractedData.skills.map((skill, index) => (
                              <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="text-yellow-800 font-medium">Please Review</p>
                            <p className="text-yellow-700">
                              This information was automatically extracted. Please verify accuracy before creating the candidate.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUpload();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors"
              >
                Cancel
              </button>
              
              {extractedData && (
                <button
                  onClick={createCandidateFromResume}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Candidate</span>
                </button>
              )}

              {uploadedFile && !extractedData && !isProcessing && (
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDashboard;